import { useEffect, useState } from 'react';
import type { GameState } from '@flip7/shared';
import { Card } from '../components/Card';
import { Avatar } from '../components/Avatar';
import { useSound } from '../hooks/useSound';
import { formatRoundEvent, eventIcon, eventColor } from '../utils/round-events';

interface RoundSummaryScreenProps {
  gameState: GameState;
  isHost: boolean;
  onNextRound: () => void;
}

interface SessionStats {
  roundsWon: number;
  bustCount: number;
  highestRoundScore: number;
}

function computeSessionStats(gameState: GameState): Map<string, SessionStats> {
  const stats = new Map<string, SessionStats>();

  for (const player of gameState.players) {
    let roundsWon = 0;
    let bustCount = 0;
    let highestRoundScore = 0;

    for (const round of player.roundHistory) {
      if (round.status === 'busted') bustCount++;
      if (round.roundScore > highestRoundScore) highestRoundScore = round.roundScore;
    }

    const totalRounds = Math.max(...gameState.players.map(p => p.roundHistory.length));
    for (let r = 0; r < totalRounds; r++) {
      let bestScore = 0;
      let winnerId = '';
      for (const p of gameState.players) {
        const rh = p.roundHistory[r];
        if (rh && rh.roundScore > bestScore) {
          bestScore = rh.roundScore;
          winnerId = p.id;
        }
      }
      if (winnerId === player.id && bestScore > 0) roundsWon++;
    }

    stats.set(player.id, { roundsWon, bustCount, highestRoundScore });
  }

  return stats;
}

export function RoundSummaryScreen({ gameState, isHost, onNextRound }: RoundSummaryScreenProps) {
  const play = useSound();
  const sortedPlayers = [...gameState.players].sort((a, b) => b.totalScore - a.totalScore);
  const [showStats, setShowStats] = useState(false);
  const sessionStats = computeSessionStats(gameState);

  useEffect(() => {
    play('roundEnd');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="screen slide-up round-summary">
      <div className="round-summary__header">
        <h2 className="round-reveal round-summary__title">Round {gameState.currentRound} Complete</h2>
        <div className="gold-divider round-summary__divider" />
      </div>

      {/* Round Events Timeline */}
      {gameState.roundEvents.length > 0 && (
        <div className="round-events">
          {gameState.roundEvents.map((event, i) => (
            <div
              key={i}
              className="fade-in round-event"
              style={{
                color: eventColor(event.type),
                animationDelay: `${i * 0.05}s`,
                animationFillMode: 'backwards',
              }}
            >
              <span className="round-event__icon">
                {eventIcon(event.type)}
              </span>
              <span>{formatRoundEvent(event)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="player-list">
        {sortedPlayers.map((player, rank) => {
          const roundState = player.roundHistory[player.roundHistory.length - 1];
          const stats = sessionStats.get(player.id);
          return (
            <div
              key={player.id}
              className={`player-card ${rank === 0 ? 'surface--accent' : 'surface'}`}
            >
              <div className="player-card__header">
                <div className="player-card__identity">
                  <span className={`rank-badge${rank === 0 ? ' rank-badge--first' : ''}`}>
                    {rank + 1}
                  </span>
                  <span className="player-card__name">
                    <Avatar config={player.avatarConfig} emoji={player.avatar} size={24} />
                    {player.name}
                    {player.title && (
                      <span className="player-card__title">
                        {player.title}
                      </span>
                    )}
                  </span>
                </div>
                <div className="player-card__score">
                  <span className="player-card__total">
                    {player.totalScore}
                  </span>
                  {roundState && (
                    <span
                      className="player-card__round-score"
                      style={{
                        color: roundState.status === 'busted' ? 'var(--color-bust)' : 'var(--color-stay)',
                      }}
                    >
                      {roundState.status === 'busted'
                        ? `(+0 BUST${roundState.bustCard?.type === 'number' ? ` \u2014 drew ${roundState.bustCard.value}` : ''})`
                        : `(+${roundState.roundScore})`}
                    </span>
                  )}
                </div>
              </div>
              {roundState && roundState.cards.length > 0 && (
                <div className="hand">
                  {roundState.cards.map((card, i) => (
                    <Card key={`${card.type}-${card.value}-${i}`} card={card} />
                  ))}
                </div>
              )}
              {/* Session stats row */}
              {showStats && stats && (
                <div className="player-card__stats">
                  <span>{stats.roundsWon}W</span>
                  <span>{stats.bustCount}B</span>
                  <span>Best: {stats.highestRoundScore}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Toggle session stats */}
      {gameState.currentRound > 1 && (
        <button
          onClick={() => setShowStats(!showStats)}
          className="link-btn"
        >
          {showStats ? 'Hide stats' : 'Show session stats'}
        </button>
      )}

      {isHost ? (
        <button className="btn btn-primary btn--full" onClick={onNextRound}>
          Next Round
        </button>
      ) : (
        <p className="text-center-muted">
          Waiting for host to start next round...
        </p>
      )}
    </div>
  );
}
